import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChartLine, Package, Receipt, Users, Tag, SignOut, Plus, Trash, Pencil, UploadSimple } from "@phosphor-icons/react";
import api from "@/lib/api";

function StatCard({ label, value, accent }) {
  return (
    <div className="card-soft p-5">
      <div className="eyebrow">{label}</div>
      <div className={`font-serif-bm text-3xl mt-2 ${accent ? "text-[var(--bm-gold)]" : ""}`}>{value}</div>
    </div>
  );
}

function ProductForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    name: "", slug: "", category: "handbags", price: 0, compare_at_price: 0,
    images: [], description: "", features: [], specifications: {}, stock: 10,
    tags: [], is_featured: false, is_bestseller: false, is_active: true, currency: "EUR"
  });
  const [imgUploading, setImgUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, price: Number(form.price), compare_at_price: Number(form.compare_at_price)||null, stock: Number(form.stock) };
      if (initial) await api.put(`/admin/products/${initial.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const upload = async (f) => {
    setImgUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, images: [...form.images, `${backend}${r.data.url}`] });
    } catch (e) { toast.error("Upload failed"); }
    setImgUploading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-auto" data-testid="product-form-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl max-w-2xl w-full p-8 space-y-3">
        <div className="flex justify-between items-center mb-2"><h3 className="font-serif-bm text-2xl">{initial ? "Edit product" : "New product"}</h3><button type="button" onClick={onClose}>×</button></div>
        <div className="grid grid-cols-2 gap-3">
          <input className="bm-input" placeholder="Name" value={form.name} onChange={e=>setForm({...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"")})} required data-testid="pf-name"/>
          <input className="bm-input" placeholder="Slug" value={form.slug} onChange={e=>setForm({...form, slug: e.target.value})} required data-testid="pf-slug"/>
          <select className="bm-input" value={form.category} onChange={e=>setForm({...form, category: e.target.value})}>
            <option value="handbags">Handbags</option><option value="wallets">Wallets</option><option value="accessories">Accessories</option>
          </select>
          <input className="bm-input" type="number" step="0.01" placeholder="Price" value={form.price} onChange={e=>setForm({...form, price: e.target.value})} required data-testid="pf-price"/>
          <input className="bm-input" type="number" step="0.01" placeholder="Compare-at price" value={form.compare_at_price||""} onChange={e=>setForm({...form, compare_at_price: e.target.value})}/>
          <input className="bm-input" type="number" placeholder="Stock" value={form.stock} onChange={e=>setForm({...form, stock: e.target.value})} data-testid="pf-stock"/>
        </div>
        <textarea className="bm-input" rows="3" placeholder="Description" value={form.description} onChange={e=>setForm({...form, description: e.target.value})}/>
        <div>
          <label className="text-xs uppercase tracking-widest">Images</label>
          <div className="flex gap-2 flex-wrap mt-2">
            {form.images.map((u, i) => <div key={i} className="relative w-16 h-20 rounded-lg overflow-hidden bg-[#f4f4f4]"><img src={u} alt="" className="w-full h-full object-cover"/><button type="button" onClick={()=>setForm({...form, images: form.images.filter((_,k)=>k!==i)})} className="absolute top-0 right-0 bg-black/60 text-white text-[10px] w-5 h-5">×</button></div>)}
            <label className="w-16 h-20 border border-dashed border-[var(--bm-border)] rounded-lg flex items-center justify-center cursor-pointer text-[var(--bm-muted)]"><UploadSimple size={18} weight="thin"/><input type="file" hidden accept="image/*" onChange={e=>e.target.files?.[0] && upload(e.target.files[0])} data-testid="pf-upload-input"/></label>
            <input className="bm-input flex-1 min-w-32" placeholder="Or paste image URL & Enter" onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); if(e.target.value){ setForm({...form, images:[...form.images, e.target.value]}); e.target.value=""; } } }}/>
          </div>
          {imgUploading && <div className="text-xs text-[var(--bm-muted)] mt-2">Uploading…</div>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.is_featured} onChange={e=>setForm({...form, is_featured: e.target.checked})}/> Featured</label>
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.is_bestseller} onChange={e=>setForm({...form, is_bestseller: e.target.checked})}/> Bestseller</label>
        </div>
        <button className="btn-primary w-full justify-center" data-testid="pf-save-btn">Save product</button>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newCoupon, setNewCoupon] = useState({ code: "", percent_off: 10, description: "", active: true });

  useEffect(() => {
    if (!localStorage.getItem("bm_admin_token")) { navigate("/admin/login"); return; }
    refresh();
    // eslint-disable-next-line
  }, []);

  const refresh = async () => {
    try {
      const [p, o, c, cp, an] = await Promise.all([
        api.get("/products", { params: { limit: 200 } }),
        api.get("/admin/orders"),
        api.get("/admin/customers"),
        api.get("/admin/coupons"),
        api.get("/admin/analytics"),
      ]);
      setProducts(p.data); setOrders(o.data); setCustomers(c.data); setCoupons(cp.data); setAnalytics(an.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    }
  };

  const logout = () => { localStorage.removeItem("bm_admin_token"); navigate("/admin/login"); };

  const delProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("Deleted"); refresh();
  };

  const addCoupon = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/coupons", { ...newCoupon, percent_off: Number(newCoupon.percent_off) }); toast.success("Coupon created"); setNewCoupon({ code: "", percent_off: 10, description: "", active: true }); refresh(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  const delCoupon = async (id) => { if(!window.confirm("Delete?")) return; await api.delete(`/admin/coupons/${id}`); refresh(); };

  const tabs = [
    { id: "dashboard", label: "Dashboard", Icon: ChartLine },
    { id: "products", label: "Products", Icon: Package },
    { id: "orders", label: "Orders", Icon: Receipt },
    { id: "customers", label: "Customers", Icon: Users },
    { id: "coupons", label: "Coupons", Icon: Tag },
  ];

  return (
    <div className="flex min-h-screen bg-[#f9f9f9]" data-testid="admin-dashboard">
      <aside className="w-60 bg-white border-r border-[var(--bm-border)] py-8 px-4 hidden md:block">
        <Link to="/" className="font-serif-bm tracking-[0.25em] block mb-8 px-2">BOOM MODE</Link>
        <nav className="space-y-1">
          {tabs.map(({id, label, Icon}) => (
            <button key={id} onClick={()=>setTab(id)} className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm ${tab===id?"bg-black text-white":"hover:bg-[#f4f4f4]"}`} data-testid={`tab-${id}`}>
              <Icon size={16} weight="thin"/> {label}
            </button>
          ))}
          <button onClick={logout} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm hover:bg-[#f4f4f4] mt-8 text-red-600" data-testid="logout-btn"><SignOut size={16} weight="thin"/> Logout</button>
        </nav>
      </aside>
      <main className="flex-1 p-6 md:p-10 overflow-x-auto">
        {tab === "dashboard" && analytics && (
          <div data-testid="dashboard-tab">
            <h1 className="font-serif-bm text-3xl mb-8">Dashboard</h1>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard label="Revenue" value={`€${analytics.total_revenue}`} accent/>
              <StatCard label="Orders" value={analytics.total_orders}/>
              <StatCard label="Customers" value={analytics.total_customers}/>
              <StatCard label="Products" value={analytics.total_products}/>
              <StatCard label="Subscribers" value={analytics.total_subscribers}/>
            </div>
            <div className="card-soft p-6 mt-6">
              <h3 className="font-serif-bm text-xl mb-4">Revenue (last 7 days)</h3>
              <div className="flex items-end gap-3 h-40">
                {(analytics.revenue_by_day || []).map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div className="w-full bg-black rounded-t-md" style={{height: `${Math.max(10, (d.revenue/(Math.max(1,...analytics.revenue_by_day.map(x=>x.revenue))))*140)}px`}}/>
                    <div className="text-[10px] text-[var(--bm-muted)] mt-1">{d.day.slice(5)}</div>
                  </div>
                ))}
                {(!analytics.revenue_by_day || analytics.revenue_by_day.length === 0) && <div className="text-sm text-[var(--bm-muted)]">No revenue yet</div>}
              </div>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div data-testid="products-tab">
            <div className="flex justify-between items-center mb-6">
              <h1 className="font-serif-bm text-3xl">Products</h1>
              <button onClick={()=>setCreating(true)} className="btn-primary" data-testid="new-product-btn"><Plus size={14} weight="thin"/> New product</button>
            </div>
            <div className="card-soft overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#f4f4f4]"><tr><th className="text-left p-3">Product</th><th className="text-left p-3">Cat</th><th className="text-left p-3">Price</th><th className="text-left p-3">Stock</th><th className="text-left p-3"></th></tr></thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p.id} className="border-t border-[var(--bm-border)]" data-testid={`row-${p.slug}`}>
                      <td className="p-3 flex items-center gap-3"><img src={p.images?.[0]} className="w-10 h-12 object-cover rounded-md"/><span className="font-serif-bm">{p.name}</span></td>
                      <td className="p-3 capitalize">{p.category}</td>
                      <td className="p-3">€{p.price}</td>
                      <td className="p-3">{p.stock}</td>
                      <td className="p-3 text-right space-x-2">
                        <button onClick={()=>setEditing(p)} className="p-1.5 border border-[var(--bm-border)] rounded-md"><Pencil size={14}/></button>
                        <button onClick={()=>delProduct(p.id)} className="p-1.5 border border-[var(--bm-border)] rounded-md text-red-600"><Trash size={14}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(creating || editing) && <ProductForm initial={editing} onClose={()=>{setEditing(null); setCreating(false);}} onSaved={()=>{setEditing(null); setCreating(false); refresh();}}/>}
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="orders-tab">
            <h1 className="font-serif-bm text-3xl mb-6">Orders</h1>
            <div className="card-soft overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#f4f4f4]"><tr><th className="text-left p-3">Order</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Country</th><th className="text-left p-3">Date</th></tr></thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id} className="border-t border-[var(--bm-border)]">
                      <td className="p-3 font-mono text-xs">{o.id.slice(0,8)}</td>
                      <td className="p-3">{o.customer_name}<div className="text-xs text-[var(--bm-muted)]">{o.customer_email}</div></td>
                      <td className="p-3">€{o.amount}</td>
                      <td className="p-3">{o.shipping_country}</td>
                      <td className="p-3 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {orders.length===0 && <tr><td colSpan={5} className="p-6 text-center text-[var(--bm-muted)]">No orders yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "customers" && (
          <div data-testid="customers-tab">
            <h1 className="font-serif-bm text-3xl mb-6">Customers</h1>
            <div className="card-soft overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#f4f4f4]"><tr><th className="text-left p-3">Email</th><th className="text-left p-3">Name</th><th className="text-left p-3">Orders</th><th className="text-left p-3">Total spent</th></tr></thead>
                <tbody>
                  {customers.map(c => (
                    <tr key={c.email} className="border-t border-[var(--bm-border)]">
                      <td className="p-3">{c.email}</td><td className="p-3">{c.name}</td><td className="p-3">{c.orders}</td><td className="p-3">€{(c.total_spent||0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "coupons" && (
          <div data-testid="coupons-tab">
            <h1 className="font-serif-bm text-3xl mb-6">Coupons</h1>
            <form onSubmit={addCoupon} className="card-soft p-5 mb-6 grid grid-cols-4 gap-3 items-end">
              <div><label className="text-xs uppercase tracking-widest">Code</label><input className="bm-input mt-1" value={newCoupon.code} onChange={e=>setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})} required data-testid="coupon-code-input"/></div>
              <div><label className="text-xs uppercase tracking-widest">% off</label><input className="bm-input mt-1" type="number" value={newCoupon.percent_off} onChange={e=>setNewCoupon({...newCoupon, percent_off: e.target.value})} required/></div>
              <div className="col-span-2"><label className="text-xs uppercase tracking-widest">Description</label><input className="bm-input mt-1" value={newCoupon.description} onChange={e=>setNewCoupon({...newCoupon, description: e.target.value})}/></div>
              <button className="btn-primary col-span-4 justify-center" data-testid="add-coupon-btn"><Plus size={14}/> Add coupon</button>
            </form>
            <div className="card-soft overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#f4f4f4]"><tr><th className="text-left p-3">Code</th><th className="text-left p-3">% off</th><th className="text-left p-3">Description</th><th></th></tr></thead>
                <tbody>
                  {coupons.map(c => (
                    <tr key={c.id} className="border-t border-[var(--bm-border)]"><td className="p-3 font-medium">{c.code}</td><td className="p-3">{c.percent_off}%</td><td className="p-3">{c.description}</td><td className="p-3 text-right"><button onClick={()=>delCoupon(c.id)} className="text-red-600"><Trash size={14}/></button></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
